import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, Box, BoxProps } from '@mui/material';
import { ConfirmProvider } from 'material-ui-confirm';
import { getToken, onMessage, isSupported } from 'firebase/messaging';
import TagManager from 'react-gtm-module';
import { toast, ToastContainer } from 'react-toastify';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga4';

import PushNotification from './components/Notification/PushNotification';
import messaging from './firebase/firebaseConfig';
import { getConfigState, getThemeMode } from './store/ConfigSlice';
import { getAuthState, userSignIn } from './store/AuthSlice';
import { useAppSelector } from './hooks';
import { LightTheme, DarkTheme } from './theme';
import ErrorAlert from './components/Alert/ErrorAlert';
import SuccessAlert from './components/Alert/SuccessAlert';
import BaseRoute from './BaseRoutes';
import {
  getAuthUser,
  getClientCredentialsTokenV2,
  saveFcmToken,
} from './store/AuthSlice/AuthApi';
import { getTrialsState, toggleBookTrialModal } from './store/V2/TrialsSlice';
import BookTrialModal from './pages/V2/TrialClass/modals/BookTrialModal';
import {
  checkUserPermission,
  getAuthUserFromLocalStorage,
  getFeedbackModalCancelledFromLocalStorage,
} from './helper';
import {
  CHAT_CONNECTION_MODE,
  getChatState,
  initiChatConnection,
  setConnected,
  verifyChatConnection,
} from './store/V2/ChatSlice';
import { getUserFlexiblePlans } from './store/V2/FlexiblePlansSlice';
import { getUserSubscriptionPlans } from './store/V2/SubscriptionPlansSlice';
import { ENV_TYPES } from './constants';
import { adminChatSetConnection } from './store/V2/AdminChatSlice';
import AppDrawer from './components/V2/AppDrawer/AppDrawer';
import WhatsAppSupportButton from './components/Button/WhatsAppSupportButton';
import { getAppTenant, getTenantsState } from './store/V2/TenantsSlice';
import { USER_PERMISSION_INDIVIDUALS } from './store/V2/RolesSlice';
import { getUsageByUserId } from './store/AccessLimitSlice/AccessLimitApi';
import socket from './helper/Socket';
import { AUTH_TYPES } from './api';
import {
  getLessonStudentFeedbacksState,
  getPendingLessonStudentFeedback,
} from './store/V2/LessonStudentFeedbacksSlice';
import ClassFeedbackModal from './components/V2/Feedback/ClassFeedbackModal';
import LottieLoader from './components/Loaders/LottieLoader';
import AppInitializationAnimation from './lottieAnimations/rocket-loader.json';
import UTMTracker from './components/V2/UTMTracker';

const containerStyles: BoxProps['sx'] = {
  backgroundColor: 'background.default',
  color: 'text.primary',
};

const TUTOPIYA_GA_TRACKING_ID =
  process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID || '';

const AIBUDDY_GA_TRACKING_ID =
  process.env.REACT_APP_AIBUDDY_GOOGLE_ANALYTICS_TRACKING_ID || '';

const App: React.FC = () => {
  const dispatch = useDispatch();
  const posthog = usePostHog();

  // Store
  const configState = useAppSelector(getConfigState);
  const themeMode = useAppSelector(getThemeMode);
  const authState = useAppSelector(getAuthState);
  const trialsState = useAppSelector(getTrialsState);
  const chatState = useAppSelector(getChatState);
  const tenantState = useAppSelector(getTenantsState);
  const lessonStudentFeedbacksState = useAppSelector(
    getLessonStudentFeedbacksState
  );

  const { user: authUser } = authState;
  const { adminEditMode } = configState;
  const { appTenant } = tenantState;
  const { selectedConversation: userChatSelectedConversation } = chatState;
  const { pendingLessonStudentFeedback } = lessonStudentFeedbacksState;

  // State
  const [initialLoading, setInitiaLoading] = useState(true);
  const [isInitiated, setInitiated] = useState(false);
  const [isPageInitiated, setPageInitiated] = useState(false);
  const [showClassFeedbackModal, setShowClassFeedbackModal] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const userRefId = searchParams.get('userRefId') || '';
  const urlArr = window.location.href.split('#');
  const newUrl = new URL(`${urlArr[0]}?${window.location.hash.substr(1)}`);
  const TPAuthToken = newUrl.searchParams.get('id_token') || undefined;
  const TPAuthLoginUserId = newUrl.searchParams.get('user_id') || undefined;

  // Prepare redirect path for reference ID login
  // Preserve current path and all params except userRefId
  const currentPath = window.location.pathname.replace(
    process.env.PUBLIC_URL || '',
    ''
  );
  const currentSearchParams = new URLSearchParams(window.location.search);
  currentSearchParams.delete('userRefId'); // Remove userRefId from params
  const redirectPath =
    currentPath +
    (currentSearchParams.toString()
      ? `?${currentSearchParams.toString()}`
      : '');
  const theme = themeMode === 'light' ? LightTheme : DarkTheme;
  const isAppInitiating =
    initialLoading || (tenantState.status === 'loading' && !appTenant);

  const aiBotForSupportVariant = useFeatureFlagVariantKey('ai_bot_for_support');
  const isAiBotForSupportTest = aiBotForSupportVariant === 'test';

  // Permissions
  const hasUsagePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      resources: [USER_PERMISSION_INDIVIDUALS.RESOURCES_USAGE],
    },
  });

  const isStudent = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      userTypes: [USER_PERMISSION_INDIVIDUALS.STUDENT],
    },
  });

  const isNotificationAllowed = (payload) => {
    if (payload?.notification?.title) {
      const _titleArr = payload.notification.title.split(' ');
      // DISABLE CHAT NOTIFICATION
      if (
        _titleArr.length > 1 &&
        _titleArr[0] === 'New' &&
        _titleArr[1] === 'Message' &&
        payload.data?.courseId === userChatSelectedConversation?.course
      ) {
        return false;
      }
    }
    return true;
  };

  onMessage(messaging, (payload) => {
    const isAllowed = isNotificationAllowed(payload);

    if (isAllowed) {
      toast(<PushNotification notification={payload.notification} />);
    }
  });

  useEffect(() => {
    initiate();
  }, []);

  useEffect(() => {
    if (isPageInitiated) {
      initAuthUser().then();
    }
  }, [authState.accessToken, isPageInitiated]);

  useEffect(() => {
    if (!isInitiated && authUser && appTenant && !authUser.role.isGuest) {
      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            course: [USER_PERMISSION_INDIVIDUALS.COURSE_CHAT],
            adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CHAT],
            class: [USER_PERMISSION_INDIVIDUALS.CLASS_CHAT],
            adminClass: [USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_CHAT],
          },
        })
      ) {
        dispatch(initiChatConnection());
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            billing: [USER_PERMISSION_INDIVIDUALS.BILLING_VIEW],
          },
        })
      ) {
        dispatch(getUserFlexiblePlans());
        dispatch(getUserSubscriptionPlans());
      }

      // Capture current feature flag variants before identify changes them.
      // posthog.identify() re-evaluates flags with the new distinct_id,
      // which can assign different variants and cause UX flicker.
      const flagsBeforeIdentify = posthog.featureFlags.getFlagVariants();

      // PostHog identify user
      posthog.identify(authUser._id, {
        name: `${authUser.firstname} ${authUser.lastname}`,
        email: authUser.email,
        role: authUser.role.name,
        tenant: appTenant?.key,
        firstname: authUser.firstname,
        lastname: authUser.lastname,
      });

      // Restore pre-login flag variants so the user's experience
      // stays consistent within this session.
      if (Object.keys(flagsBeforeIdentify).length > 0) {
        posthog.featureFlags.override(flagsBeforeIdentify);
      }

      dispatch(getPendingLessonStudentFeedback());

      setInitiated(true);
    }
  }, [authUser, appTenant]);

  useEffect(() => {
    if (authUser && hasUsagePermission) {
      dispatch(getUsageByUserId(authUser._id));
    }
  }, [authUser?._id, hasUsagePermission]);

  useEffect(() => {
    if (chatState.initiated) {
      dispatch(
        verifyChatConnection({
          isAdminModeEnabled: adminEditMode,
        })
      );
    }
  }, [chatState.initiated]);

  useEffect(() => {
    if (chatState.chatConnectionMode === CHAT_CONNECTION_MODE.ADMIN) {
      dispatch(adminChatSetConnection(true));
    } else if (chatState.chatConnectionMode === CHAT_CONNECTION_MODE.USER) {
      dispatch(setConnected(true));
    } else {
      dispatch(adminChatSetConnection(false));
      dispatch(setConnected(false));
    }
  }, [chatState.chatConnectionMode]);

  useEffect(() => {
    if (pendingLessonStudentFeedback?._id) {
      const feedbackModalCancelled = getFeedbackModalCancelledFromLocalStorage();
      if (feedbackModalCancelled !== pendingLessonStudentFeedback._id) {
        setShowClassFeedbackModal(true);
      }
    } else {
      setShowClassFeedbackModal(false);
    }
  }, [pendingLessonStudentFeedback]);

  const initiate = async () => {
    const { hostname } = window.location;
    // ReactGA.pageview(window.location.pathname + window.location.search);

    if (hostname === 'www.tutopiya.com') {
      TagManager.initialize({ gtmId: 'GTM-WCC337Q' });
      ReactGA.initialize(TUTOPIYA_GA_TRACKING_ID);
    } else if (hostname === 'www.aibuddy.sg') {
      TagManager.initialize({ gtmId: 'GTM-MQFBQSXT' });
      ReactGA.initialize(AIBUDDY_GA_TRACKING_ID);
    }

    await dispatch(getAppTenant());

    if (TPAuthToken && TPAuthLoginUserId) {
      await dispatch(
        userSignIn({
          auth_type: AUTH_TYPES.TP_TOKEN,
          auth_token: TPAuthToken,
          user_id: TPAuthLoginUserId,
        })
      );
    } else if (userRefId) {
      if (!authState.accessToken || !authState.user) {
        await dispatch(
          userSignIn({
            auth_type: AUTH_TYPES.REF_ID,
            refid: userRefId,
            redirectPath,
          })
        );
      } else {
        // Remove userRefId from URL without page refresh
        const url = new URL(window.location.href);
        url.searchParams.delete('userRefId');
        window.history.replaceState({}, '', url.toString());
      }
    } else if (!authState.accessToken) {
      await dispatch(getClientCredentialsTokenV2());
    }

    setPageInitiated(true);
  };

  async function requestPermission() {
    const isSupportedBrowser = await isSupported();

    if (isSupportedBrowser) {
      try {
        // Unregister existing service workers to avoid conflicts
        const existingRegistrations = await navigator.serviceWorker.getRegistrations();
        console.log('Existing registrations:', existingRegistrations.length);

        // eslint-disable-next-line no-restricted-syntax
        for (const registration of existingRegistrations) {
          // eslint-disable-next-line no-await-in-loop
          await registration.unregister();
          console.log('Unregistered existing service worker');
        }
        // Register the service worker
        const serviceWorkerRegistration = await navigator.serviceWorker.register(
          `${process.env.PUBLIC_URL}/firebase-messaging-sw.js`
        );

        // Wait for the service worker to activate with a timeout
        const waitForActivation = new Promise<ServiceWorkerRegistration>(
          (resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Service worker activation timeout'));
            }, 10000); // 10 second timeout

            if (serviceWorkerRegistration.active) {
              clearTimeout(timeout);
              resolve(serviceWorkerRegistration);
            } else if (serviceWorkerRegistration.installing) {
              serviceWorkerRegistration.installing.onstatechange = (event) => {
                const sw = event.target as ServiceWorker;
                if (sw.state === 'activated') {
                  clearTimeout(timeout);
                  resolve(serviceWorkerRegistration);
                }
              };
            } else {
              // Wait for ready
              navigator.serviceWorker.ready.then((reg) => {
                clearTimeout(timeout);
                resolve(reg);
              });
            }
          }
        );

        await waitForActivation;

        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
          const token = await getToken(messaging, {
            serviceWorkerRegistration,
          });

          dispatch(saveFcmToken({ token }));
        } else if (permission === 'denied') {
          // notifications are blocked
          // eslint-disable-next-line no-console
          console.log('You denied for the notification');
        }
      } catch (error) {
        console.log('Error in requestPermission:', error);
      }
    }
  }

  const initAuthUser = async () => {
    if (authState.accessToken && authState.user) {
      await dispatch(getAuthUser());

      const localStorageAuthUser = getAuthUserFromLocalStorage();

      if (localStorageAuthUser && localStorageAuthUser._id) {
        await socket.connect();
      }

      if (
        authState.user &&
        !authState.user.role.isGuest &&
        process.env.REACT_APP_ENV !== ENV_TYPES.LOCAL
      ) {
        await requestPermission().then();
      }

      setInitiaLoading(false);
    } else if (authState.accessToken) {
      setInitiaLoading(false);
    }
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <ConfirmProvider>
          {isAppInitiating ? (
            <LottieLoader
              lottieJson={AppInitializationAnimation}
              loadingMessages={['Launching your learning experience…']}
            />
          ) : (
            <Box
              data-testid="main-app-container"
              className="App"
              sx={containerStyles}
            >
              <BrowserRouter>
                <UTMTracker />
                <Routes>
                  <Route
                    path={`${process.env.PUBLIC_URL}/*`}
                    element={<BaseRoute />}
                  />
                </Routes>
              </BrowserRouter>
              <ErrorAlert />
              <SuccessAlert />
              {/* MODALS */}
              {trialsState.bookTrialModalOpen && (
                <BookTrialModal
                  open={trialsState.bookTrialModalOpen}
                  onClose={() => dispatch(toggleBookTrialModal())}
                />
              )}
              {pendingLessonStudentFeedback && (
                <ClassFeedbackModal
                  open={showClassFeedbackModal}
                  onClose={() => setShowClassFeedbackModal(false)}
                  classData={pendingLessonStudentFeedback}
                />
              )}
            </Box>
          )}
        </ConfirmProvider>
      </ThemeProvider>
      <ToastContainer />
      {authUser && isStudent && isAiBotForSupportTest && <AppDrawer />}
      {authUser && (!isStudent || !isAiBotForSupportTest) && (
        <WhatsAppSupportButton />
      )}
    </>
  );
};

export default App;
